import { ReactNode, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { clsx } from 'clsx'
import { Button, MealBackgroundStyle } from '@tovala/component-library'
import { MenuComponentsStandardized } from '@tovala/browser-apis-menu-components'
import {
  getFilteredComponentsCounts,
  MealGroup as MealGroupData,
} from 'utils/menus'
import { HEADING_CLASS_MAP, SUBHEADING_CLASS_MAP } from './backgroundStyles'
import { FilterTheme } from './filterThemes'

const MenuMeals = ({
  everythingElseCollapse,
  filteredGroupTheme,
  filters,
  mealGroups,
  menuComponentsGrid,
}: {
  everythingElseCollapse?: {
    hiddenMealsCount: number
    onClickShowAll(): void
  } | null
  filteredGroupTheme?: FilterTheme | null
  filters?: ReactNode
  mealGroups: MealGroupData[]
  menuComponentsGrid(opts: {
    backgroundStyle: MealBackgroundStyle
    components: MenuComponentsStandardized
  }): ReactNode
}) => {
  const prefersReducedMotion = useReducedMotion()
  const isCollapsed = !!everythingElseCollapse

  // Derived during render rather than in an effect so the revealed section is
  // already at its starting opacity on the first painted frame.
  const [prevIsCollapsed, setPrevIsCollapsed] = useState(isCollapsed)
  const [shouldAnimateReveal, setShouldAnimateReveal] = useState(false)

  if (prevIsCollapsed !== isCollapsed) {
    setPrevIsCollapsed(isCollapsed)
    setShouldAnimateReveal(prevIsCollapsed && !isCollapsed)
  }

  const animateReveal = shouldAnimateReveal && !prefersReducedMotion

  // getMealGroups only emits a filtered group when filters are applied, so its
  // absence is how we know to hide the divider and the Everything Else banner.
  const filteredGroup = mealGroups.find((group) => group.id === 'filtered')
  const filteredMealsCount = filteredGroup
    ? getFilteredComponentsCounts({ components: filteredGroup.components })
    : 0

  const visibleMealGroups = mealGroups.filter(
    (group) =>
      !(
        isCollapsed &&
        group.id === 'everythingElse' &&
        group.components.length === 0
      ),
  )

  // While collapsed, what's left of "Everything Else" reads as part of the
  // filtered section, so it carries that section's theme rather than
  // interrupting the band with the default background.
  const getGroupTheme = (id: string): FilterTheme | null =>
    id === 'filtered' || (isCollapsed && id === 'everythingElse')
      ? (filteredGroupTheme ?? null)
      : null

  const lastGroup = visibleMealGroups.at(-1)
  const hasThemedTop = !!getGroupTheme(visibleMealGroups[0]?.id ?? '')
  const hasThemedBottom = !!lastGroup && !!getGroupTheme(lastGroup.id)

  return (
    <div
      className={clsx(
        !hasThemedTop && 'pt-10 md:pt-6',
        !hasThemedBottom && 'pb-52 md:pb-40',
      )}
    >
      {visibleMealGroups.map(({ components, description, id, title }) => {
        const theme = getGroupTheme(id)
        const backgroundStyle: MealBackgroundStyle = theme ? 'dark' : 'light'

        const isEverythingElseWithFilters =
          !!filteredGroup && id === 'everythingElse'
        // Collapsing nulls out the group's title, so the banner stays hidden
        // until the rest of the menu is revealed.
        const hasBanner = isEverythingElseWithFilters && !!title

        // Remounting on reveal is what lets the freshly shown section run its
        // enter animation; every other render keeps the same key and stays put.
        const revealKey =
          id === 'everythingElse' && animateReveal ? 'revealed' : 'static'

        return (
          <MealGroup key={id} hasLeadingBand={hasBanner} theme={theme}>
            <motion.div
              key={revealKey}
              animate={{ opacity: 1, y: 0 }}
              initial={
                id === 'everythingElse' && animateReveal
                  ? { opacity: 0, y: 12 }
                  : false
              }
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              {hasBanner && (
                <EverythingElseBanner
                  mealsOutsideFiltersCount={getFilteredComponentsCounts({
                    components,
                  })}
                  title={title}
                />
              )}

              <div className="lg:px-4 md:px-0">
                <div className="space-y-10 md:space-y-6">
                  {!isEverythingElseWithFilters && (
                    <MealGroupTitle
                      backgroundStyle={backgroundStyle}
                      description={description}
                      filters={filters && id === 'filtered' ? filters : null}
                      quantityResults={
                        id === 'filtered' ? filteredMealsCount : null
                      }
                      title={title}
                    />
                  )}

                  {menuComponentsGrid({ backgroundStyle, components })}

                  {id === 'filtered' &&
                    filteredMealsCount > 0 &&
                    !isCollapsed && (
                      <EndOfFilteredMealsDivider
                        backgroundStyle={backgroundStyle}
                        count={filteredMealsCount}
                      />
                    )}

                  {everythingElseCollapse &&
                    everythingElseCollapse.hiddenMealsCount > 0 &&
                    id === 'filtered' && (
                      <ShowAllMealsCard
                        hiddenMealsCount={
                          everythingElseCollapse.hiddenMealsCount
                        }
                        onClickShowAll={everythingElseCollapse.onClickShowAll}
                      />
                    )}
                </div>
              </div>
            </motion.div>
          </MealGroup>
        )
      })}
    </div>
  )
}

export default MenuMeals

const ShowAllMealsCard = ({
  hiddenMealsCount,
  onClickShowAll,
}: {
  hiddenMealsCount: number
  onClickShowAll(): void
}) => {
  return (
    <div className="mx-auto max-w-menu">
      <div className="rounded-lg bg-navy-faded px-10 py-12 text-center md:rounded-none md:px-4 md:py-8">
        <h2 className="text-k/28_130 md:text-k/20_110">
          There&apos;s more on the menu.
        </h2>

        <p className="mx-auto mt-4 max-w-[560px] text-k/20_110 leading-[140%] text-grey-9 md:text-k/16_125">
          Longevity meals are just part of what Tovala cooks each week. Browse
          the rest anytime.
        </p>

        <div className="mt-8 flex justify-center md:mt-6">
          <Button buttonStyle="dark" onClick={onClickShowAll} size="medium">
            Show {hiddenMealsCount} more{' '}
            {hiddenMealsCount === 1 ? 'meal' : 'meals'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const MealGroup = ({
  children,
  hasLeadingBand,
  theme,
}: {
  children: ReactNode
  hasLeadingBand: boolean
  theme: FilterTheme | null
}) => {
  return (
    <div
      className={clsx(
        // The band fades out rather than snapping when a group stops being
        // themed, which happens as "Everything Else" is revealed.
        'transition-colors duration-300',
        theme ? 'py-10 md:pt-6' : 'py-10 md:py-5',
        // A full-bleed band leads the group flush against the section above it,
        // so the group's own top padding would show as a seam of page
        // background between the two bands.
        hasLeadingBand && 'pt-0 md:pt-0',
        // A themed group opts out of the edge padding resets: the colored band
        // needs its full padding so it never sits flush against the heading.
        theme ? theme.backgroundClassName : 'first:pt-0 last:pb-0 even:pb-10',
        // A themed group that closes the menu takes over the runway keeping the
        // last meals clear of the fixed order status bar, so the band runs to
        // the bottom of the page rather than stopping short of it.
        theme && 'last:pb-52 md:last:pb-40',
      )}
    >
      {children}
    </div>
  )
}

const DIVIDER_LINE_CLASS_MAP: { [key in MealBackgroundStyle]: string } = {
  dark: 'bg-grey-8',
  light: 'bg-grey-6',
  tinted: 'bg-grey-6',
}

const DIVIDER_TEXT_CLASS_MAP: { [key in MealBackgroundStyle]: string } = {
  dark: 'text-grey-3',
  light: 'text-grey-10',
  tinted: 'text-grey-10',
}

const EverythingElseBanner = ({
  mealsOutsideFiltersCount,
  title,
}: {
  mealsOutsideFiltersCount: number
  title: string
}) => {
  return (
    <div className="mb-10 bg-black px-4 py-10 text-white md:mb-6">
      <div className="mx-auto max-w-menu space-y-2">
        <h2 className="text-k/36_110 md:text-k/26_110">{title}</h2>

        <p className="text-k/20_125 md:text-k/18_120">
          {mealsOutsideFiltersCount} meals this week outside your filters
        </p>
      </div>
    </div>
  )
}

const EndOfFilteredMealsDivider = ({
  backgroundStyle,
  count,
}: {
  backgroundStyle: MealBackgroundStyle
  count: number
}) => {
  const lineClasses = clsx('h-px grow', DIVIDER_LINE_CLASS_MAP[backgroundStyle])

  return (
    <div className="mx-auto hidden max-w-menu items-center gap-x-3 md:flex md:px-4">
      <div className={lineClasses} />

      <p
        className={clsx(
          'text-k/16_125',
          DIVIDER_TEXT_CLASS_MAP[backgroundStyle],
        )}
      >
        End of your {count} filtered meals
      </p>

      <div className={lineClasses} />
    </div>
  )
}

const MealGroupTitle = ({
  backgroundStyle,
  description,
  filters,
  quantityResults,
  title,
}: {
  backgroundStyle: MealBackgroundStyle
  description: string | null
  filters: ReactNode
  quantityResults: number | null
  title: string | null
}) => {
  if (!title) {
    return null
  }

  const subheadingClasses = clsx(
    'text-k/20_125 md:text-k/18_120',
    SUBHEADING_CLASS_MAP[backgroundStyle],
  )

  return (
    <div className="mx-auto max-w-menu space-y-3 md:px-4">
      <div className="flex items-center justify-between">
        <h2
          className={clsx(
            'text-k/36_110 md:text-k/26_110',
            HEADING_CLASS_MAP[backgroundStyle],
          )}
        >
          {title}
        </h2>

        {filters}
      </div>

      <div className="space-y-10">
        {quantityResults !== null && (
          <p className={subheadingClasses}>{quantityResults} results</p>
        )}

        {description && <div className={subheadingClasses}>{description}</div>}
      </div>
    </div>
  )
}
